import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { Switch } from "../../../ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../ui/collapsible";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import { Tooltip } from "../../../ui/tooltip";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { 
  Plus, 
  Trash2, 
  MessageSquare,
  Settings,
  HelpCircle,
  AlertTriangle,
  MoreHorizontal,
  Info,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { 
  ConfigSidebarState, 
  InterjectionGroup, 
  InterjectionType,
  InterjectionContent,
} from "../utils/types";
import { cn } from "../../../../lib/utils";

interface InterjectionsTabProps {
  configState: ConfigSidebarState;
  onStartAddingNewGroup: () => void;
  onStartEditingGroup: (group: InterjectionGroup) => void;
  onCancelEditing: () => void;
  onSaveInterjectionGroup: (groupData: {
    type: InterjectionType;
    customTypeName?: string;
    groupName?: string;
    interjection_rate: number;
    within_script: boolean;
    repeat_script: boolean;
  }) => void;
  onDeleteInterjectionGroup: (group: InterjectionGroup) => void;
  onStartAddingContent: (groupId: string) => void;
  onStartEditingContent: (groupId: string, contentId: string) => void;
  onSaveContent: (interjection_text: string, interjection_response: string) => void;
  onSaveContentForGroup?: (groupId: string, interjection_text: string, interjection_response: string) => void;
  onDeleteContent: (groupId: string, contentId: string) => void;
}

const InterjectionsTab: React.FC<InterjectionsTabProps> = ({
  configState,
  onStartAddingNewGroup,
  onStartEditingGroup,
  onCancelEditing,
  onSaveInterjectionGroup,
  onDeleteInterjectionGroup,
  onStartAddingContent,
  onStartEditingContent,
  onSaveContent,
  onDeleteContent,
  onSaveContentForGroup,
}) => {
  // Form state for group configuration
  const [groupFormData, setGroupFormData] = useState({
    type: "FAQ" as InterjectionType,
    customTypeName: "",
    groupName: "",
    interjection_rate_bucket: "rarely", // Changed to bucket-based
    within_script: false,
    repeat_script: true,
  });
  
  // Form validation errors
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Track current form type for conditional rendering
  const [currentFormType, setCurrentFormType] = useState<InterjectionType>("FAQ");
  
  // Helper function to convert group name to clean variable name
  const generateCustomTypeName = useCallback((groupName: string): string => {
    return groupName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
      .trim()
      .replace(/\s+/g, '_'); // Replace spaces with underscores
  }, []);

  // Interjection rate bucket mappings
  const interjectionRateBuckets = [
    { label: "Rarely", value: "rarely", percentage: 25 },
    { label: "Moderately", value: "moderately", percentage: 50 },
    { label: "Often", value: "often", percentage: 75 }
  ];

  // Helper functions for interjection rate conversion
  const getInterjectionRateBucket = useCallback((percentage: number): string => {
    if (percentage <= 25) return "rarely";
    if (percentage <= 50) return "moderately";
    return "often";
  }, []);

  const getInterjectionRatePercentage = useCallback((bucket: string): number => {
    const bucketData = interjectionRateBuckets.find(b => b.value === bucket);
    return bucketData ? bucketData.percentage : 50; // Default to moderate
  }, []);

  const getInterjectionRateBucketLabel = useCallback((percentage: number): string => {
    const bucket = getInterjectionRateBucket(percentage);
    const bucketData = interjectionRateBuckets.find(b => b.value === bucket);
    return bucketData ? bucketData.label : "Moderately";
  }, [getInterjectionRateBucket]);

  // Local state to track editing values for each content item
  const [editingValues, setEditingValues] = useState<Record<string, { interjection_text: string; interjection_response: string }>>({});

  // Local state to track which group is adding new content
  const [addingContentToGroup, setAddingContentToGroup] = useState<string | null>(null);
  const [newContentData, setNewContentData] = useState({
    interjection_text: "",
    interjection_response: "",
  });

  // State for collapsible content panels
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

  // Initialize form with default values
  React.useEffect(() => {
    if (!configState.editingGroup && !configState.isAddingNewGroup) {
      setCurrentFormType("FAQ");
      setGroupFormData({
        type: "FAQ",
        customTypeName: "",
        groupName: "",
        interjection_rate_bucket: "rarely", // Default to rarely
        within_script: false,
        repeat_script: true,
      });
      setFormErrors({});
    }
  }, [configState.editingGroup, configState.isAddingNewGroup]);

  // Initialize editing values when groups change
  useEffect(() => {
    const newEditingValues: Record<string, { interjection_text: string; interjection_response: string }> = {};
    configState.groups.forEach((group: InterjectionGroup) => {
      group.content.forEach((content: InterjectionContent) => {
        const uniqueKey = `${group.id}-${content.id}`;
        newEditingValues[uniqueKey] = {
          interjection_text: content.interjection_text,
          interjection_response: content.interjection_response,
        };
      });
    });
    setEditingValues(newEditingValues);
  }, [configState.groups]);

  // Reset forms when editing states change
  useEffect(() => {
    if (configState.editingGroup) {
      const group = configState.editingGroup;
      const groupName = group.groupName || "";
      setGroupFormData({
        type: group.type,
        customTypeName: group.customTypeName || generateCustomTypeName(groupName), // Preserve existing customTypeName else generate from group name
        groupName: groupName,
        interjection_rate_bucket: getInterjectionRateBucket(group.interjection_rate * 100),
        within_script: group.within_script,
        repeat_script: group.repeat_script,
      });
      setCurrentFormType(group.type);
      setFormErrors({});
    } else if (configState.isAddingNewGroup) {
      setGroupFormData({
        type: "FAQ",
        customTypeName: "",
        groupName: "",
        interjection_rate_bucket: "rarely",
        within_script: false,
        repeat_script: true,
      });
      setCurrentFormType("FAQ");
      setFormErrors({});
    }
  }, [configState.editingGroup, configState.isAddingNewGroup, generateCustomTypeName, getInterjectionRateBucket]);

  // Function to save content changes
  const handleSaveContentChanges = (groupId: string, contentId: string) => {
    const uniqueKey = `${groupId}-${contentId}`;
    const values = editingValues[uniqueKey];
    if (!values) return;
    
    // Set up the editing context and save
    onStartEditingContent(groupId, contentId);
    setTimeout(() => {
      onSaveContent(values.interjection_text, values.interjection_response);
    }, 0);
  };

  // Function to handle adding new content
  const handleStartAddingContent = (groupId: string) => {
    setAddingContentToGroup(groupId);
    setNewContentData({
      interjection_text: "",
      interjection_response: "",
    });
  };

  // Function to save new content
  const handleSaveNewContent = (groupId: string) => {
    if (onSaveContentForGroup) {
      onSaveContentForGroup(groupId, newContentData.interjection_text, newContentData.interjection_response);
      setAddingContentToGroup(null);
      setNewContentData({ interjection_text: "", interjection_response: "" });
    } else {
      onStartAddingContent(groupId);
      setTimeout(() => {
        onSaveContent(newContentData.interjection_text, newContentData.interjection_response);
        setAddingContentToGroup(null);
        setNewContentData({ interjection_text: "", interjection_response: "" });
      }, 0);
    }
  };

  // Function to cancel adding content
  const handleCancelAddingContent = () => {
    setAddingContentToGroup(null);
    setNewContentData({
      interjection_text: "",
      interjection_response: "",
    });
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!groupFormData.type) {
      errors.type = "Please select a type";
    }
    
    if (!groupFormData.groupName.trim()) {
      errors.groupName = "Please enter a group name";
    }
    
    // Note: customTypeName is now auto-generated from groupName, so no validation needed
    
    if (!groupFormData.interjection_rate_bucket) {
      errors.interjection_rate_bucket = "Please select an interjection rate";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveGroup = () => {
    if (!validateForm()) {
      return; // Validation failed
    }
    
    onSaveInterjectionGroup({
      type: groupFormData.type,
      customTypeName: groupFormData.customTypeName,
      groupName: groupFormData.groupName,
      interjection_rate: getInterjectionRatePercentage(groupFormData.interjection_rate_bucket) / 100, // Convert bucket to decimal
      within_script: groupFormData.within_script,
      repeat_script: groupFormData.repeat_script,
    });
  };

  const getTypeIcon = (type: InterjectionType) => {
    switch (type) {
      case "interruption":
        return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case "FAQ":
        return <HelpCircle className="w-4 h-4 text-blue-500" />;
      case "other":
        return <MoreHorizontal className="w-4 h-4 text-purple-500" />;
      default:
        return <MessageSquare className="w-4 h-4 text-green-500" />;
    }
  };

  const getTypeColor = (type: InterjectionType) => {
    switch (type) {
      case "interruption":
        return "orange";
      case "FAQ":
        return "blue";
      case "other":
        return "purple";
      default:
        return "green";
    }
  };

  const showGroupForm = configState.isAddingNewGroup || configState.editingGroup;

  return (
    <div className="h-[calc(100vh-160px)] overflow-y-auto overflow-x-hidden p-6">
      {/* Add New Group Button */}
      {!showGroupForm && (
        <Button
          onClick={onStartAddingNewGroup}
          className="w-full mb-5 h-8 bg-foreground hover:bg-foreground/90 text-background"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create New Interjection Group
        </Button>
      )}

      {/* Group Configuration Form */}
      {showGroupForm && (
        <Card className="mb-5 border-border bg-accent/10">
          <CardHeader className="pb-4 bg-accent/10 border-b border-border">
            <CardTitle className="flex items-center gap-2 text-sm font-gc-med text-foreground">
              <Settings className="w-5 h-5 text-maitai-green" />
              {configState.editingGroup ? "Edit" : "Create"} Interjection Group
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Type Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {(["interruption", "FAQ", "other"] as InterjectionType[]).map((type) => (
                  <Button
                    key={type}
                    variant={groupFormData.type === type ? "default" : "outline"}
                    size="sm"
                    className={cn(
                      "justify-start gap-2",
                      groupFormData.type === type && "bg-foreground text-background"
                    )}
                    onClick={() => {
                      setGroupFormData(prev => ({ ...prev, type }));
                      setCurrentFormType(type);
                      setFormErrors(prev => ({ ...prev, type: "" }));
                    }}
                  >
                    {getTypeIcon(type)}
                    {type === "interruption" ? "Interruption" : type === "FAQ" ? "FAQ" : "Other"}
                  </Button>
                ))}
              </div>
              {formErrors.type && (
                <p className="text-xs text-destructive">{formErrors.type}</p>
              )}
            </div>

            {/* Group Name */}
            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">Group Name</Label>
              <Input
                value={groupFormData.groupName}
                onChange={(e) => {
                  const newGroupName = e.target.value;
                  const autoCustomTypeName = generateCustomTypeName(newGroupName);
                  setGroupFormData(prev => ({ 
                    ...prev, 
                    groupName: newGroupName,
                    customTypeName: autoCustomTypeName
                  }));
                  setFormErrors(prev => ({ ...prev, groupName: "" }));
                }}
                placeholder="Enter a name for this group..."
                className={cn(formErrors.groupName && "border-destructive")}
              />
              {formErrors.groupName && (
                <p className="text-xs text-destructive">{formErrors.groupName}</p>
              )}
            </div>



            {/* Interjection Rate */}
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label className="text-sm font-gc-med text-foreground">Interjection Rate</Label>
                <Tooltip content="How often you expect the conversation to be interjected with this type of content" side="right" immediate={true}>
                  <div>
                    <HelpCircle className="w-3 h-3 text-maitai-green hover:text-maitai-green/80 transition-colors" />
                  </div>
                </Tooltip>
              </div>
              <RadioGroup 
                value={groupFormData.interjection_rate_bucket} 
                onValueChange={(value) => {
                  setGroupFormData(prev => ({ ...prev, interjection_rate_bucket: value }));
                  setFormErrors(prev => ({ ...prev, interjection_rate_bucket: "" }));
                }}
                className="flex justify-between"
              >
                {interjectionRateBuckets.map((bucket) => (
                  <div key={bucket.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={bucket.value} id={bucket.value} />
                    <Label htmlFor={bucket.value} className="text-sm font-gc-reg cursor-pointer">
                      {bucket.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {formErrors.interjection_rate_bucket && (
                <p className="text-xs text-destructive">{formErrors.interjection_rate_bucket}</p>
              )}
            </div>

            {/* Configuration Switches */}
            <div className="space-y-4">
              <Label className="text-sm font-gc-med text-foreground">Configuration</Label>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={groupFormData.within_script}
                    onCheckedChange={(checked) => setGroupFormData(prev => ({ ...prev, within_script: checked }))}
                  />
                  <span className="text-xs font-gc-reg text-foreground">Within Script</span>
                  <Tooltip content="Whether the bot can be interjected mid-sentence (usually helpful for voice applications)" side="right" immediate={true}>
                    <div>
                      <Info className="w-3 h-3 text-maitai-green hover:text-maitai-green/80 transition-colors" />
                    </div>
                  </Tooltip>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={groupFormData.repeat_script}
                    onCheckedChange={(checked) => setGroupFormData(prev => ({ ...prev, repeat_script: checked }))}
                  />
                  <span className="text-xs font-gc-reg text-foreground">Repeat Script</span>
                  <Tooltip content="Whether the bot should repeat the initial conversation lines before it was interjected after responding to the interjection" side="left" immediate={true}>
                    <div>
                      <Info className="w-3 h-3 text-maitai-green hover:text-maitai-green/80 transition-colors" />
                    </div>
                  </Tooltip>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                onClick={handleSaveGroup}
                className="h-8 bg-foreground hover:bg-foreground/90 text-background"
              >
                Save Group
              </Button>
              <Button 
                variant="outline" 
                onClick={onCancelEditing}
                className="h-8"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {configState.groups.length === 0 && !showGroupForm && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="space-y-3">
            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto" />
            <h3 className="text-lg font-gc-med text-foreground">
              No interjection groups found
            </h3>
            <p className="text-muted-foreground text-sm max-w-md">
              Create your first group to get started
            </p>
          </div>
        </div>
      )}

      {/* Groups List */}
      {configState.groups.length > 0 && !showGroupForm && (
        <div className="space-y-4">
          {configState.groups.map((group: InterjectionGroup) => (
            <Card
              key={group.id}
              className={cn(
                "border-border shadow-sm",
                `border-l-4 border-l-${getTypeColor(group.type)}-500`
              )}
            >
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(group.type)}
                    <h3 className="font-gc-med text-foreground">
                      {group.groupName || (group.type === "other" ? group.customTypeName : group.type.toUpperCase())}
                    </h3>
                    <Badge variant="secondary">
                      {group.content.length} items
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      className="h-6 w-6 p-0"
                      onClick={() => onStartEditingGroup(group)}
                    >
                      <Settings className="w-3 h-3" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => onDeleteInterjectionGroup(group)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Configuration Summary */}
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">
                    Rate: {getInterjectionRateBucketLabel(group.interjection_rate * 100)}
                  </Badge>
                  <Badge variant={group.within_script ? "default" : "secondary"}>
                    {group.within_script ? "Cuts Response" : "No Cut"}
                  </Badge>
                  <Badge variant={group.repeat_script ? "default" : "secondary"}>
                    {group.repeat_script ? "Repeats" : "No Repeat"}
                  </Badge>
                </div>
                
                <div className="h-px bg-border" />
                
                {/* Content Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-gc-med text-foreground">
                      Content ({group.content.length})
                    </h4>
                    <Button
                      variant="outline"
                      onClick={() => handleStartAddingContent(group.id)}
                      className="h-6 px-2 gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add Content
                    </Button>
                  </div>
                  
                  {group.content.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div className="space-y-2">
                        <MessageSquare className="w-8 h-8 text-muted-foreground mx-auto" />
                        <p className="text-xs text-muted-foreground">No content yet</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {group.content.map((content: InterjectionContent) => {
                        const panelKey = `${group.id}-${content.id}`;
                        const isExpanded = expandedPanels.includes(panelKey);
                        
                        return (
                          <Collapsible 
                            key={content.id}
                            open={isExpanded}
                            onOpenChange={(open) => {
                              if (open) {
                                setExpandedPanels(prev => [...prev, panelKey]);
                              } else {
                                setExpandedPanels(prev => prev.filter(k => k !== panelKey));
                              }
                            }}
                          >
                            <CollapsibleTrigger className="flex items-center justify-between w-full p-3 text-left bg-accent/20 hover:bg-accent/40 rounded-md transition-colors">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                )}
                                <p className="text-xs font-gc-reg text-foreground truncate">
                                  {content.interjection_text || "No text"}
                                </p>
                              </div>
                              <Button 
                                variant="ghost" 
                                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteContent(group.id, content.id);
                                }}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </CollapsibleTrigger>
                            
                            <CollapsibleContent>
                              <div className="pl-4 pt-4 border-l-2 border-border ml-2">
                                {/* Always show editable text areas when expanded */}
                                <div className="space-y-4">
                                  <div>
                                    <Label className="text-xs font-gc-med text-muted-foreground uppercase tracking-wide mb-2 block">
                                      Interjection Text:
                                    </Label>
                                    <Textarea
                                      rows={3}
                                      value={editingValues[`${group.id}-${content.id}`]?.interjection_text || ""}
                                      onChange={(e) => setEditingValues(prev => ({
                                        ...prev,
                                        [`${group.id}-${content.id}`]: {
                                          ...prev[`${group.id}-${content.id}`],
                                          interjection_text: e.target.value
                                        }
                                      }))}
                                      placeholder="Enter the interjection text..."
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs font-gc-med text-muted-foreground uppercase tracking-wide mb-2 block">
                                      Interjection Response:
                                    </Label>
                                    <Textarea
                                      rows={3}
                                      value={editingValues[`${group.id}-${content.id}`]?.interjection_response || ""}
                                      onChange={(e) => setEditingValues(prev => ({
                                        ...prev,
                                        [`${group.id}-${content.id}`]: {
                                          ...prev[`${group.id}-${content.id}`],
                                          interjection_response: e.target.value
                                        }
                                      }))}
                                      placeholder="Enter the interjection response..."
                                    />
                                  </div>
                                  <div className="text-right">
                                    <Button 
                                      onClick={() => handleSaveContentChanges(group.id, content.id)}
                                      className="h-6 px-2 bg-foreground hover:bg-foreground/90 text-background"
                                    >
                                      Save Changes
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Add new content inline */}
                  {addingContentToGroup === group.id && (
                    <div className="mt-3 p-4 bg-accent/20 border border-border rounded-md">
                      <div className="space-y-4">
                        <div>
                          <Label className="text-xs font-gc-med text-muted-foreground uppercase tracking-wide mb-2 block">
                            Interjection Text:
                          </Label>
                          <Textarea
                            rows={3}
                            value={newContentData.interjection_text}
                            onChange={(e) => setNewContentData(prev => ({ ...prev, interjection_text: e.target.value }))}
                            placeholder="Enter the interjection text..."
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-gc-med text-muted-foreground uppercase tracking-wide mb-2 block">
                            Interjection Response:
                          </Label>
                          <Textarea
                            rows={3}
                            value={newContentData.interjection_response}
                            onChange={(e) => setNewContentData(prev => ({ ...prev, interjection_response: e.target.value }))}
                            placeholder="Enter the interjection response..."
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline"
                            onClick={handleCancelAddingContent}
                            className="h-6 px-2"
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={() => handleSaveNewContent(group.id)}
                            className="h-6 px-2 bg-foreground hover:bg-foreground/90 text-background"
                          >
                            Add Content
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default InterjectionsTab;